import { useState, useCallback, useEffect, useMemo } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { formatDate, formatNumber, formatPercent } from '@/ui/format'
import { StatusBadge } from '@/components/StatusBadge'
import { StatCard, Select, Input, Button, Table, Pagination, Alert, Badge, DatePicker } from '@/ui'
import styles from './AIActivityHistory.module.css'

// --- 타입 ---

interface ActivityUser {
  email: string
  name: string | null
}

interface AIActivity {
  id: string
  userId: string
  type: 'generation' | 'batch_job' | 'theme'
  status: string
  modelId: string | null
  style: string | null
  role: string | null
  creditsUsed: number | null
  cost: number | null
  inputTokens: number | null
  outputTokens: number | null
  estimatedTokenCost: number | null
  createdAt: string
  completedAt: string | null
  details: Record<string, unknown> | null
  user: ActivityUser
}

interface AIActivityResponse {
  activities: AIActivity[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: {
    totalCount: number
    totalCost: number
    avgCost: number
    failRate: number
    totalInputTokens: number
    totalOutputTokens: number
    totalEstimatedTokenCost: number
  }
}

// --- 상수 ---

const TYPE_OPTIONS = [
  { value: '', label: '전체 유형' },
  { value: 'generation', label: '이미지 생성' },
  { value: 'batch_job', label: '배치 잡' },
  { value: 'theme', label: '테마' },
]

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'pending', label: 'pending' },
  { value: 'processing', label: 'processing' },
  { value: 'completed', label: 'completed' },
  { value: 'partial', label: 'partial' },
  { value: 'failed', label: 'failed' },
  { value: 'cancelled', label: 'cancelled' },
]

const STYLE_OPTIONS = [
  { value: '', label: '전체 스타일' },
  { value: 'CLASSIC_STUDIO', label: '클래식 스튜디오' },
  { value: 'OUTDOOR_GARDEN', label: '아웃도어 가든' },
  { value: 'SUNSET_BEACH', label: '선셋 비치' },
  { value: 'TRADITIONAL_HANBOK', label: '전통 한복' },
  { value: 'VINTAGE_CINEMATIC', label: '빈티지 시네마' },
  { value: 'LUXURY_HOTEL', label: '럭셔리 호텔' },
  { value: 'CITY_LIFESTYLE', label: '시티 라이프' },
  { value: 'ENCHANTED_FOREST', label: '숲속' },
  { value: 'BLACK_AND_WHITE', label: '흑백' },
  { value: 'MINIMALIST_GALLERY', label: '미니멀 갤러리' },
]

const MODEL_OPTIONS = [
  { value: '', label: '전체 모델' },
  { value: 'gpt-image-1', label: 'GPT Image 1' },
  { value: 'dall-e-3', label: 'DALL-E 3' },
  { value: 'gpt-image-1.5', label: 'GPT Image 1.5' },
  { value: 'gemini-flash-image', label: 'Gemini Flash' },
  { value: 'gemini-3-pro-image', label: 'Gemini 3 Pro' },
]

const ADMIN_TEST_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'true', label: '테스트만' },
  { value: 'false', label: '일반만' },
]

const TYPE_BADGE_COLORS: Record<string, 'blue' | 'green' | 'yellow'> = {
  generation: 'blue',
  batch_job: 'green',
  theme: 'yellow',
}

const TYPE_LABELS: Record<string, string> = {
  generation: '이미지',
  batch_job: '배치',
  theme: '테마',
}

// --- 컴포넌트 ---

export function AIActivityHistory() {
  const { filters, setFilters, resetFilters } = useAdminFilters()

  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [styleFilter, setStyleFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [adminTestFilter, setAdminTestFilter] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  // 300ms debounce for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchInput)
      setFilters({ page: 1 })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, setFilters])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(filters.page))
    p.set('pageSize', String(filters.pageSize))
    p.set('sortBy', filters.sortBy)
    p.set('sortOrder', filters.sortOrder)
    if (typeFilter) p.set('type', typeFilter)
    if (statusFilter) p.set('status', statusFilter)
    if (styleFilter) p.set('style', styleFilter)
    if (modelFilter) p.set('modelId', modelFilter)
    if (searchDebounced) p.set('search', searchDebounced)
    if (startDate) p.set('startDate', new Date(startDate).toISOString())
    if (endDate) p.set('endDate', new Date(endDate + 'T23:59:59').toISOString())
    if (adminTestFilter) p.set('isAdminTest', adminTestFilter)
    return p.toString()
  }, [filters, typeFilter, statusFilter, styleFilter, modelFilter, searchDebounced, startDate, endDate, adminTestFilter])

  const { data, isLoading, error } = useApiFetch<AIActivityResponse>(
    `/api/admin/ai-activity-history?${params}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const handleResetFilters = useCallback(() => {
    setTypeFilter('')
    setStatusFilter('')
    setStyleFilter('')
    setModelFilter('')
    setSearchInput('')
    setSearchDebounced('')
    setStartDate('')
    setEndDate('')
    setAdminTestFilter('')
    resetFilters()
  }, [resetFilters])

  const toggleExpand = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id)
  }, [])

  const columns: any[] = [
    {
      key: 'expand',
      label: '',
      width: '36px',
      render: (row: AIActivity) => (
        <button
          className={styles.expandBtn}
          onClick={(e) => { e.stopPropagation(); toggleExpand(row.id) }}
          title="상세 보기"
        >
          {expandedId === row.id ? '▼' : '▶'}
        </button>
      ),
    },
    {
      key: 'type',
      label: '유형',
      width: '70px',
      render: (row: AIActivity) => (
        <Badge color={TYPE_BADGE_COLORS[row.type] ?? 'gray'} className={styles.typeBadge}>
          {TYPE_LABELS[row.type] ?? row.type}
        </Badge>
      ),
    },
    {
      key: 'userName',
      label: '유저',
      render: (row: AIActivity) => (
        <span>
          {row.user?.name ?? row.user?.email ?? '-'}
          {row.role === 'ADMIN' && <> <Badge color="yellow">테스트</Badge></>}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      sortable: true,
      render: (row: AIActivity) => <StatusBadge status={row.status} />,
      width: '100px',
    },
    {
      key: 'modelId',
      label: '모델',
      render: (row: AIActivity) => row.modelId ?? '-',
      width: '140px',
    },
    {
      key: 'style',
      label: '스타일',
      render: (row: AIActivity) => row.style ?? '-',
    },
    {
      key: 'creditsUsed',
      label: '크레딧',
      sortable: true,
      render: (row: AIActivity) => row.creditsUsed != null ? String(row.creditsUsed) : '-',
      align: 'right' as const,
      width: '80px',
    },
    {
      key: 'cost',
      label: '비용($)',
      sortable: true,
      render: (row: AIActivity) => row.cost != null ? `$${row.cost.toFixed(4)}` : '-',
      align: 'right' as const,
      width: '90px',
    },
    {
      key: 'tokens',
      label: '토큰(in/out)',
      render: (row: AIActivity) =>
        row.inputTokens != null || row.outputTokens != null
          ? `${formatNumber(row.inputTokens ?? 0)} / ${formatNumber(row.outputTokens ?? 0)}`
          : '—',
    },
    {
      key: 'estimatedTokenCost',
      label: '토큰추정($)',
      render: (row: AIActivity) =>
        row.estimatedTokenCost != null ? `$${row.estimatedTokenCost.toFixed(4)}` : '—',
    },
    {
      key: 'createdAt',
      label: '일시',
      sortable: true,
      render: (row: AIActivity) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'),
      width: '130px',
    },
  ]

  // 확장 행에 표시할 상세 정보 렌더링
  const renderExpandedRow = (row: AIActivity) => {
    if (expandedId !== row.id) return null

    return (
      <tr key={`${row.id}-detail`}>
        <td colSpan={columns.length} className={styles.detailRow}>
          <div className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>ID:</span>
              <span className={styles.detailValue}>{row.id}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>유저 ID:</span>
              <span className={styles.detailValue}>{row.userId}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>이메일:</span>
              <span className={styles.detailValue}>{row.user.email}</span>
            </div>
            {row.completedAt && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>완료:</span>
                <span className={styles.detailValue}>{formatDate(row.completedAt, 'yyyy.M.d HH:mm:ss')}</span>
              </div>
            )}
          </div>
          {row.details && Object.keys(row.details).length > 0 && (
            <pre className={styles.detailJson}>
              {JSON.stringify(row.details, null, 2)}
            </pre>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div className={styles.root}>
      {/* 통계 카드 */}
      <div className={styles.statGrid}>
        <StatCard label="총 활동 수" value={formatNumber(data?.stats.totalCount ?? 0)} />
        <StatCard label="총 비용" value={`$${(data?.stats.totalCost ?? 0).toFixed(2)}`} />
        <StatCard
          label="토큰 추정 비용"
          value={`$${(data?.stats.totalEstimatedTokenCost ?? 0).toFixed(2)}`}
        />
        <StatCard label="평균 비용" value={`$${(data?.stats.avgCost ?? 0).toFixed(4)}`} />
        <StatCard label="실패율" value={formatPercent(data?.stats.failRate ?? 0)} />
      </div>

      {/* 필터 */}
      <div className={styles.filterGrid}>
        <div className={styles.filterRow}>
          <Input
            size="sm"
            placeholder="유저 이메일/이름 검색"
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
          />
          <Select
            size="sm"
            value={typeFilter}
            onChange={(e: any) => { setTypeFilter(e.target.value); setFilters({ page: 1 }) }}
            options={TYPE_OPTIONS}
          />
          <Select
            size="sm"
            value={statusFilter}
            onChange={(e: any) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }}
            options={STATUS_OPTIONS}
          />
          <Select
            size="sm"
            value={styleFilter}
            onChange={(e: any) => { setStyleFilter(e.target.value); setFilters({ page: 1 }) }}
            options={STYLE_OPTIONS}
          />
        </div>
        <div className={styles.filterRow}>
          <Select
            size="sm"
            value={modelFilter}
            onChange={(e: any) => { setModelFilter(e.target.value); setFilters({ page: 1 }) }}
            options={MODEL_OPTIONS}
          />
          <DatePicker
            size="sm"
            value={startDate}
            onChange={(_date: Date | null, formatted: string) => { setStartDate(formatted); setFilters({ page: 1 }) }}
            placeholder="시작일"
          />
          <DatePicker
            size="sm"
            value={endDate}
            onChange={(_date: Date | null, formatted: string) => { setEndDate(formatted); setFilters({ page: 1 }) }}
            placeholder="종료일"
          />
          <Select
            size="sm"
            value={adminTestFilter}
            onChange={(e: any) => { setAdminTestFilter(e.target.value); setFilters({ page: 1 }) }}
            options={ADMIN_TEST_OPTIONS}
          />
          <Button size="sm" variant="ghost" onClick={handleResetFilters}>
            필터 초기화
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* 테이블 */}
      <Table
        columns={columns}
        data={data?.activities ?? []}
        rowKey={(row: AIActivity) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
        renderAfterRow={renderExpandedRow}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page: number) => setFilters({ page })}
          size="sm"
        />
      )}
    </div>
  )
}
